import React, { useState } from "react"
import Text from "aldoo-ra/Text"
import Typography from "aldoo-ra/Typography"
import Value from "aldoo-ra/Value"
import UserProfileButton from "./UserProfileButton"
import MenuGroup from "aldoo-ra/MenuGroup"
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline"


// Module categories based on common patterns
const getModuleCategory = (name) => {
  if (!name) return 'content'
  const lowerName = name.toLowerCase()
  if (lowerName.includes('spam') || lowerName.includes('backup') || lowerName.includes('update') || lowerName.includes('platform') || lowerName.includes('environments')) {
    return 'security'
  }
  if (lowerName.includes('newsletter') || lowerName.includes('mail') || lowerName.includes('comment')) {
    return 'communication'
  }
  if (lowerName.includes('analytics') || lowerName.includes('param') || lowerName.includes('sitemap') || lowerName.includes('logs') || lowerName.includes('changes') || lowerName.includes('task')) {
    return 'analytics'
  }
  if (lowerName.includes('store') || lowerName.includes('shop') || lowerName.includes('license')) {
    return 'commerce'
  }
  if (lowerName.includes('themes')) {
    return 'style'
  }
  if (lowerName.includes('structure')) {
    return 'structure'
  }
  if (lowerName.includes('support') || lowerName.includes('agents') || lowerName.includes('help')) {
    return 'assistance'
  }
  return 'content' // Default for CMS, Blog, etc.
}

const CompactModuleCard = ({ module, isSelected, onClick, updates, showEnterHint }) => {
  const category = getModuleCategory(module.label)
  
  // Get category-based gradient colors
  const getIconGradient = (category) => {
    switch (category) {
      case 'content':
        return 'bg-gradient-to-br from-blue-500 to-blue-700'
      case 'security':
        return 'bg-gradient-to-br from-red-500 to-red-700'
      case 'assistance':
        return 'bg-gradient-to-br from-orange-400 to-orange-600'
      case 'analytics':
        return 'bg-gradient-to-br from-green-500 to-green-700'
      case 'commerce':
        return 'bg-gradient-to-br from-yellow-400 to-yellow-600'
      case 'style':
        return 'bg-gradient-to-br from-violet-500 to-violet-700'
      case 'communication':
        return 'bg-gradient-to-br from-sky-400 to-sky-600'
      case 'structure':
        return 'bg-gradient-to-br from-lime-400 to-lime-600'
      default:
        return 'bg-gradient-to-br from-admin_primary to-admin_primary_dk'
    }
  }

  return (
    <div className="relative w-full">
      <div
        className={`
          relative overflow-hidden group 
          w-full h-20 min-h-20 max-h-20
          bg-admin_paper rounded-xl shadow-sm
          cursor-pointer transition-all duration-300
          hover:transform hover:-translate-y-1 hover:scale-105 hover:shadow-md
          ${isSelected ? 'border-admin_primary shadow-lg' : ''}
        `}
        onClick={() => onClick(module)}
      >
      {/* Animated shimmer effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-admin_neutral_1/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500 pointer-events-none" />
      
      {/* Fixed inner container with minimal horizontal padding */}
      <div className="flex items-center h-full p-4 gap-2">
        {/* Icon container - left aligned with background color */}
        <div className={`
          w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 relative z-10
          ${getIconGradient(category)}
        `}>
          {module.image ? (
            <img
              src={module.image}
              alt={module.label}
              className="w-8 h-8 object-contain filter brightness-0 invert"
            />
          ) : (
            <DocumentTextIcon className="w-8 h-8 text-white" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 relative z-10">
          <div className="font-semibold text-base text-admin_text truncate leading-tight h-6 flex items-center">
            {module.label}
          </div>
        </div>

        {/* Update badge */}
        {updates > 0 && (
          <div className="w-5 h-5 bg-admin_error text-white rounded-full flex items-center justify-center text-xs font-medium relative z-10 flex-shrink-0">
            {updates > 9 ? "9+" : updates}
          </div>
        )}
      </div>

      {/* Tooltip */}
      {module.description && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-30 max-w-48 text-center leading-snug">
          {module.description}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
      </div>

      {/* Enter hint for single filtered item */}
      {showEnterHint && (
        <div className="mt-2 text-center">
          <Typography variant="admin_body" className="text-admin_text_secondary font-medium text-sm">
            Press <kbd className="bg-admin_neutral_1 border border-admin_bordered rounded px-2 py-1 text-xs font-mono">Enter</kbd> to open
          </Typography>
        </div>
      )}
    </div>
  )
}

const CompactAdminHome = () => {
  const [updates] = Value("site-updates")
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedModule, setSelectedModule] = useState(null)
  const [menuData, setMenuData] = useState(null)


  const handleModuleClick = (module) => {
    setSelectedModule(module)
    if (module.link && module.link[0]) {
      // Handle both formats: direct url and nested content.url
      const url = module.link[0].url || module.link[0].content?.url
      if (url) {
        window.location.href = url
      }
    }
  }

  // Calculate filtered items from menu data
  const filteredItems = menuData?.content?.items?.filter(item => {
    if (!searchTerm.trim()) return true
    return item.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
  }) || []

  const filteredItemsCount = searchTerm.trim() ? filteredItems.length : 0
  const singleFilteredItem = filteredItemsCount === 1 ? filteredItems[0] : null


  // Handle Enter key press in search input
  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter' && filteredItemsCount === 1 && singleFilteredItem) {
      event.preventDefault()
      
      // Use the content property which has the actual menu item structure
      const actualMenuItem = singleFilteredItem.content
      
      if (actualMenuItem && actualMenuItem.link && actualMenuItem.link[0] && actualMenuItem.link[0].content && actualMenuItem.link[0].content.url) {
        const url = actualMenuItem.link[0].content.url
        window.location.href = url
      }
    }
  }


  return (
    <div className="min-h-screen bg-admin_canvas">
      <div className="max-w-7xl mx-auto p-8">
        {/* Header with Title and Search */}
        <div className="mb-8">
          <div className="bg-admin_paper rounded-2xl shadow-sm p-6">
            <div className="flex items-center gap-6">
              {/* Title */}
              <a href="/" className="flex-shrink-0 cursor-pointer hover:opacity-75 transition-opacity">
                <Text
                  id="site_name"
                  variant="admin_h1"
                  fallback="Admin Panel"
                />
              </a>
              
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-admin_text_secondary" />

                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search features..."
                    autoFocus
                    className="w-full pl-12 pr-12 py-4 border border-admin_bordered rounded-xl text-base font-medium transition-all duration-200 focus:outline-none focus:border-admin_primary bg-admin_neutral text-admin_text placeholder-admin_text_secondary"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 hover:bg-admin_neutral_1 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4 text-admin_text_secondary hover:text-admin_text" />
                    </button>
                  )}
                </div>
              </div>

              {/* Top Right Controls */}
              <div className="flex items-center gap-4 flex-shrink-0 z-20">
                <UserProfileButton />
              </div>
            </div>
          </div>
        </div>

        {/* Search results indicator */}
        {searchTerm && (
          <div className="mb-4 text-left">
            <Typography variant="admin_body" className="text-admin_text_secondary font-medium">
              Searching for "{searchTerm}"
            </Typography>
          </div>
        )}

        {/* MenuGroup with compact cards */}
        <MenuGroup
          name="AdminSiteFeatures"
          renderAs="hlist"
          styleOverrides={{
            menu: "grid gap-1 grid-cols-1 md:grid-cols-3 lg:grid-cols-4",
            menu_item: "flex items-center list-none p-1.5 rounded-md text-admin_text my-0",
          }}
          skipRootItem
          onData={({ data }) => {
            // Store menu data for filtering calculations
            if (data && data[0]) {
              setMenuData(data[0])
            }
          }}
          preProcessItem={(menuItem) => {
            // Filter items based on search term before rendering
            if (menuItem.items && searchTerm.trim()) {
              menuItem.items = menuItem.items.filter(item => 
                item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
              )
            }
            return menuItem
          }}
          menuItemRender={(item) => {
            // Add update badge for Updates module
            if (item.name === "AdminUpdates") {
              item.addOn = () => {
                if (!updates) return null
                return (
                  <div className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-admin_error text-white text-md font-medium -mt-1 -mr-1">
                    {updates > 99 ? "99+" : updates}
                  </div>
                )
              }
            }
            
            const showHint = filteredItemsCount === 1 && singleFilteredItem?.label === item.name
            
            return (
              <CompactModuleCard
                module={item}
                isSelected={selectedModule?.label === item.label}
                onClick={handleModuleClick}
                updates={item.name === "AdminUpdates" ? updates : 0}
                showEnterHint={showHint}
              />
            )
          }}
        />
      </div>
    </div>
  )
}

export default CompactAdminHome